/**
 * PollingContext - Context for managing polling state across entity sync components
 *
 * Allows any component to enable polling (e.g., when syncing or when suggestions are in progress),
 * and all child components using the context will poll for updated data.
 */

'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

// ============================================================================
// Types
// ============================================================================

interface PollingContextValue {
  /** Whether polling is currently active */
  isPolling: boolean;
  /** The polling interval in ms (0 means no polling) */
  pollingInterval: number;
  /** Start polling - increments the polling request count */
  startPolling: () => void;
  /** Stop polling - decrements the polling request count */
  stopPolling: () => void;
  /** Set polling for a specific reason (automatically stops when component unmounts) */
  usePollingEffect: (shouldPoll: boolean) => void;
}

// ============================================================================
// Context
// ============================================================================

const PollingContext = createContext<PollingContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface PollingProviderProps {
  children: React.ReactNode;
  /** Default polling interval in ms when polling is active */
  interval?: number;
}

export const PollingProvider: React.FC<PollingProviderProps> = ({
  children,
  interval = 2000,
}) => {
  // Count of active polling requests (multiple components can request polling)
  const [pollingRequestCount, setPollingRequestCount] = useState(0);

  const startPolling = useCallback(() => {
    setPollingRequestCount((prev) => prev + 1);
  }, []);

  const stopPolling = useCallback(() => {
    setPollingRequestCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Custom hook for components to easily manage polling based on a condition
  const usePollingEffect = useCallback(
    (shouldPoll: boolean) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const hasStartedRef = useRef(false);

      // eslint-disable-next-line react-hooks/rules-of-hooks
      React.useEffect(() => {
        if (shouldPoll && !hasStartedRef.current) {
          hasStartedRef.current = true;
          startPolling();
        } else if (!shouldPoll && hasStartedRef.current) {
          hasStartedRef.current = false;
          stopPolling();
        }

        return () => {
          if (hasStartedRef.current) {
            hasStartedRef.current = false;
            stopPolling();
          }
        };
      }, [shouldPoll]);
    },
    [startPolling, stopPolling],
  );

  const value = useMemo(
    (): PollingContextValue => ({
      isPolling: pollingRequestCount > 0,
      pollingInterval: pollingRequestCount > 0 ? interval : 0,
      startPolling,
      stopPolling,
      usePollingEffect,
    }),
    [
      pollingRequestCount,
      interval,
      startPolling,
      stopPolling,
      usePollingEffect,
    ],
  );

  return (
    <PollingContext.Provider value={value}>{children}</PollingContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access polling context
 */
export function usePolling(): PollingContextValue {
  const context = useContext(PollingContext);
  if (!context) {
    // Return a no-op version if used outside provider
    return {
      isPolling: false,
      pollingInterval: 0,
      startPolling: () => {},
      stopPolling: () => {},
      usePollingEffect: () => {},
    };
  }
  return context;
}

/**
 * Hook to enable polling while a condition is true
 * Automatically starts/stops polling based on the condition
 */
export function usePollingWhile(shouldPoll: boolean): void {
  const { startPolling, stopPolling } = usePolling();
  const hasStartedRef = useRef(false);

  React.useEffect(() => {
    if (shouldPoll && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startPolling();
    } else if (!shouldPoll && hasStartedRef.current) {
      hasStartedRef.current = false;
      stopPolling();
    }

    return () => {
      if (hasStartedRef.current) {
        hasStartedRef.current = false;
        stopPolling();
      }
    };
  }, [shouldPoll, startPolling, stopPolling]);
}

export default PollingContext;
